export const CHAT_CONFIG = {
  MESSAGE_LIMIT: 50,
  MAX_MESSAGE_LENGTH: 1000,
  AUTO_SCROLL: false,
  SHOW_TYPING_INDICATOR: true,
  SHOW_READ_RECEIPTS: true,
  ENABLE_REACTIONS: false,
  TIME_FORMAT: '12hr' as '12hr' | '24hr',
  TYPING_DEBOUNCE: 300,
  TYPING_TIMEOUT: 3000,
  DATE_FORMAT: 'MM/DD/YYYY',
} as const

export const CHAT_LABELS = {
  ADMIN: {
    PAGE_TITLE: 'Message Admin',
    PAGE_DESCRIPTION:
      "Start a conversation with the sponsor regarding their ads. All messages will be logged under the sponsor's profile for future reference.",
    INPUT_PLACEHOLDER: 'Type your message…',
    EMOJI_BUTTON_LABEL: 'Add emoji',
    LINK_BUTTON_LABEL: 'Add link',
    SEND_BUTTON_LABEL: 'Send message',
    ATTACH_BUTTON_LABEL: 'Attach image',
  },
  SPONSOR: {
    PAGE_TITLE: 'Message Sponsor',
    PAGE_DESCRIPTION:
      'Chat with the admin regarding your ads. All messages are logged for reference.',
    INPUT_PLACEHOLDER: 'Type your message…',
    EMOJI_BUTTON_LABEL: 'Add emoji',
    LINK_BUTTON_LABEL: 'Add link',
    SEND_BUTTON_LABEL: 'Send message',
    ATTACH_BUTTON_LABEL: 'Attach image',
  },
  COMMON: {
    LOADING: 'Loading messages...',
    ERROR: 'Failed to load messages',
    NO_MESSAGES: 'No messages yet. Start a conversation!',
    TYPING: 'typing...',
    SEARCH_EMOJI_PLACEHOLDER: 'Search emoji',
    ONLINE: 'Online',
    OFFLINE: 'Offline',
    LAST_SEEN: 'Last seen {time}',
  },
} as const

export const CHAT_ERRORS = {
  SEND_FAILED: 'Failed to send message. Please try again.',
  LOAD_FAILED: 'Failed to load messages. Please refresh the page.',
  NETWORK_ERROR: 'Network error. Please check your connection.',
  EMPTY_MESSAGE: 'Please enter a message',
  MESSAGE_TOO_LONG: `Message is too long. Maximum ${CHAT_CONFIG.MAX_MESSAGE_LENGTH} characters.`,
  SERVICE_UNAVAILABLE: 'Chat service is currently unavailable',
  UNAUTHORIZED_ADMIN:
    "You don't have authorization to send messages in this conversation.",
  ADMIN_CHANGED:
    'The chat admin has been changed. Please refresh to connect with the new admin.',
  ROOM_NOT_AUTHORIZED:
    'This conversation is no longer available. The message admin may have been changed.',
} as const

export function formatChatTime(
  timestamp: number,
  format: '12hr' | '24hr' = CHAT_CONFIG.TIME_FORMAT
): string {
  const date = new Date(timestamp)
  const options: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: format === '12hr',
  }
  return date.toLocaleTimeString('en-US', options)
}

export function groupMessagesByDate<T extends { timestamp: number }>(
  messages: T[]
): Map<string, T[]> {
  const grouped = new Map<string, T[]>()

  messages.forEach(message => {
    const date = new Date(message.timestamp)
    const dateKey = date.toLocaleDateString('en-US', {
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
    })

    if (!grouped.has(dateKey)) {
      grouped.set(dateKey, [])
    }
    grouped.get(dateKey)!.push(message)
  })

  return grouped
}

export function isToday(timestamp: number): boolean {
  const messageDate = new Date(timestamp)
  const today = new Date()
  return (
    messageDate.getDate() === today.getDate() &&
    messageDate.getMonth() === today.getMonth() &&
    messageDate.getFullYear() === today.getFullYear()
  )
}

export function isYesterday(timestamp: number): boolean {
  const messageDate = new Date(timestamp)
  const yesterday = new Date()
  yesterday.setDate(yesterday.getDate() - 1)
  return (
    messageDate.getDate() === yesterday.getDate() &&
    messageDate.getMonth() === yesterday.getMonth() &&
    messageDate.getFullYear() === yesterday.getFullYear()
  )
}

export function formatDateSeparator(timestamp: number): string {
  if (isToday(timestamp)) {
    return 'Today'
  }
  if (isYesterday(timestamp)) {
    return 'Yesterday'
  }
  const date = new Date(timestamp)
  return date.toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
}

export const CHATROOM_PATH = 'chatRooms'
export const USERS_COLLECTION = 'users'
export const CHAT_PATHSEGMENTS = {
  MESSAGES: 'messages',
  TYPING: 'typing',
  PRESENCE: 'presence',
}

export const CHAT_FIELDS = {
  SENDERID: 'senderId',
  READ: 'read',
  TIMESTAMP: 'timestamp',
  PARTICIPANTS: 'participants',
  CHAT_ADMIN_ID: 'chatAdminId',
  LAST_MESSAGE: 'lastMessage',
  LAST_MESSAGE_TIME: 'lastMessageTime',
}

export const CHAT_OBSERVER_OPTIONS = {
  root: null,
  rootMargin: '0px',
  threshold: 0.5,
}

export const DUMMY_ADMIN_ID = import.meta.env.VITE_DUMMY_MESSAGE_ADMIN_ID
